import { supabase } from '@/lib/supabase/client';

export type AccessTokenResult =
	| { status: 'ok'; token: string }
	| { status: 'no-session' }
	| { status: 'error'; message: string };

export const TOKEN_ERROR_MESSAGE =
	'Die Sitzung konnte nicht geprüft werden. Bitte lade die Seite neu.';

let inflight: Promise< AccessTokenResult > | null = null;

function withTimeout< T >( p: Promise< T >, ms: number ): Promise< T > {
	return new Promise( ( resolve, reject ) => {
		const t = (
			typeof window !== 'undefined' ? window : globalThis
		).setTimeout( () => {
			reject( new Error( `Timeout after ${ ms }ms` ) );
		}, ms );
		p.then(
			( v ) => {
				( typeof window !== 'undefined'
					? window
					: globalThis
				).clearTimeout( t );
				resolve( v );
			},
			( e ) => {
				( typeof window !== 'undefined'
					? window
					: globalThis
				).clearTimeout( t );
				reject( e );
			}
		);
	} );
}

/**
 * Returns a valid Supabase access token, refreshing the session if needed.
 *
 * The result distinguishes an authoritative logged-out state (`no-session`)
 * from a retrieval failure such as a timeout or storage error (`error`), so
 * callers can render the logged-out path vs. a visible error state.
 */
export async function getValidAccessToken(): Promise< AccessTokenResult > {
	if ( inflight ) return inflight;

	inflight = ( async (): Promise< AccessTokenResult > => {
		try {
			// `getSession()` can hang in some browser/storage/privacy setups.
			const { data, error } = await withTimeout(
				supabase.auth.getSession(),
				5000
			);
			if ( ! error ) {
				if ( data.session?.access_token ) {
					return {
						status: 'ok',
						token: data.session.access_token,
					};
				}
				// getSession resolved cleanly with no session: logged out.
				return { status: 'no-session' };
			}

			// getSession errored: attempt a refresh (also timeboxed).
			const refreshed = await withTimeout(
				supabase.auth.refreshSession(),
				8000
			);
			if ( refreshed.data.session?.access_token ) {
				return {
					status: 'ok',
					token: refreshed.data.session.access_token,
				};
			}
			if (
				refreshed.error?.name === 'AuthSessionMissingError' ||
				refreshed.error?.code === 'session_expired' ||
				refreshed.error?.code === 'refresh_token_not_found'
			) {
				// No usable refresh token: authoritatively logged out.
				return { status: 'no-session' };
			}
			return {
				status: 'error',
				message: refreshed.error?.message ?? TOKEN_ERROR_MESSAGE,
			};
		} catch {
			// Timeout or unexpected throw: retrieval failed, the member may
			// still have a valid session.
			return { status: 'error', message: TOKEN_ERROR_MESSAGE };
		} finally {
			inflight = null;
		}
	} )();

	return inflight;
}
